/**
 * Canadian postal-code lookup for the location search.
 *
 * ## What was wrong
 *
 * The landing page's postal search geocoded a code against OpenStreetMap
 * Nominatim, flew the map to it, dropped a pin — and then sent the customer to
 * a bare `/s`. The coordinates were never put in the search URL, so someone who
 * typed their postal code and pressed "Browse All Vehicles" got **every listing
 * in Canada**. It looked like a location search and was not one.
 *
 * Three further faults in the lookup itself:
 *
 *   - **No timeout.** `fetch` with no AbortController. A hung provider left the
 *     spinner spinning with no way out, because `setLoading(false)` sits after
 *     the await.
 *   - **No retry**, so one dropped packet was a user-visible failure.
 *   - **Full postal codes only.** Canada Post's full six-character data is
 *     proprietary; OpenStreetMap's coverage of it is patchy, while coverage of
 *     the three-character Forward Sortation Area is good. Asking only for
 *     `R3C0V8` means some codes resolve and some do not, for reasons invisible
 *     to the person typing — which is exactly what "unreliable" describes.
 *
 * ## Which parameter actually filters
 *
 * `bounds`, not `origin`. `isOriginInUse` in util/search.js requires
 * `config.maps.search.sortSearchByDistance`, and configMaps.js sets that to
 * `false` — so SearchPage.duck.js drops `origin` on the floor. A fix built
 * around `origin` would have looked right, tested green against the URL, and
 * returned unfiltered results.
 *
 * ## What this module is not
 *
 * Not a second search engine, not a second geocoder, not a database. It
 * normalises a code, asks the existing provider, and builds a URL for the
 * existing search page with the existing `bounds` parameter.
 */

/**
 * Canada Post format. First position excludes D, F, I, O, Q, U, W and Z; the
 * other letter positions exclude D, F, I, O, Q and U.
 *
 * Unchanged from the landing page's own regex, which was already correct —
 * worth saying, because "fix the validation" was the obvious guess and would
 * have been a regression.
 */
export const POSTAL_RE = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z] ?\d[ABCEGHJ-NPRSTV-Z]\d$/i;

/** The three-character Forward Sortation Area on its own. */
export const FSA_RE = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z]$/i;

/**
 * Normalise anything a person might type.
 *
 * Accepts lower case, missing or extra spaces, hyphens, and surrounding
 * whitespace — all of which are how postal codes are actually written down.
 *
 * @returns {{valid: Boolean, postal: String|null, fsa: String|null}}
 */
export const normalizePostal = input => {
  if (typeof input !== 'string') return { valid: false, postal: null, fsa: null };

  // Strip everything that is not alphanumeric: "r3c-0v8", "R3C 0V8" and
  // "r3c0v8" are the same code.
  //
  // Deliberately NOT truncated to six characters. Trimming "R3C 0V8X" down to
  // a valid "R3C0V8" would accept a typo and send the customer somewhere they
  // did not ask for, which is worse than telling them the code is wrong.
  const compact = input.toUpperCase().replace(/[^A-Z0-9]/g, '');

  if (!POSTAL_RE.test(compact)) {
    // A bare FSA is a legitimate thing to type, and enough to search with.
    if (FSA_RE.test(compact)) {
      return { valid: true, postal: null, fsa: compact };
    }
    return { valid: false, postal: null, fsa: null };
  }

  // Canonical display form, the way Canada Post writes it.
  return {
    valid: true,
    postal: `${compact.slice(0, 3)} ${compact.slice(3)}`,
    fsa: compact.slice(0, 3),
  };
};

/** Metres per degree of latitude. Good to a fraction of a percent anywhere. */
const METRES_PER_DEGREE_LAT = 111320;

/**
 * A square bounding box of `radiusMetres` around a point.
 *
 * Deliberately not the Google Maps helper in util/googleMaps.js: that one calls
 * `new window.google.maps.Circle(...)`, and this runs on a page that loads
 * Leaflet. A dependency on an SDK that is not present would fail at the moment
 * a customer searches.
 *
 * Longitude degrees shrink with latitude, which matters in Canada — a degree of
 * longitude at Iqaluit is less than half what it is at Windsor, so a fixed
 * degree offset would make the box far too wide in the north.
 *
 * @returns {{ne: {lat, lng}, sw: {lat, lng}}}
 */
export const boundsAround = (lat, lng, radiusMetres) => {
  // A radius that is not a finite number has no box. Degrading to the whole
  // world keeps a caller's bug a wide search rather than NaN corners, which
  // SearchPage would forward to the API as the string "NaN".
  const radius = Number.isFinite(radiusMetres) && radiusMetres > 0 ? radiusMetres : Infinity;

  const latDelta = radius / METRES_PER_DEGREE_LAT;

  // A degree of longitude shrinks with latitude, and in Canada that is a large
  // effect: at 84° it is a tenth of what it is at the southern border.
  const cos = Math.cos((lat * Math.PI) / 180);
  const lngDelta = cos > 0 ? radius / (METRES_PER_DEGREE_LAT * cos) : Infinity;

  // Clamped, never wrapped.
  //
  // Wrapping was the obvious thing to write and is wrong here. Near a pole the
  // longitude offset exceeds a half-turn, and a wrapped offset puts `sw.lng`
  // *east* of `ne.lng` — a box that excludes the point it was asked to
  // surround. There is also no delta threshold that fixes it, which is the trap
  // I fell into first: whether wrapping breaks the box depends on the longitude
  // as well as the offset, so at 89.5° a 103° offset is under any half-turn
  // test and still wraps to a box from 160°E to 6°E.
  //
  // Clamping is monotone and always contains the centre. Its one cost is a box
  // truncated at the dateline for a point beside it, which Canada never is:
  // its eastern limit plus the widest offset this can produce is still 43°W.
  const clamp = (v, limit) => Math.max(-limit, Math.min(limit, v));
  const latBand = { ne: clamp(lat + latDelta, 90), sw: clamp(lat - latDelta, 90) };

  // An offset of a half-turn or more means the circle provably reaches every
  // meridian, so the exact answer is the full longitude range. Clamping alone
  // would give a box truncated on one side — containing the centre, but
  // narrower than the truth. This branch is only ever an accuracy improvement:
  // correctness rests on the clamp above, not on where this threshold sits.
  if (lngDelta >= 180) {
    return { ne: { lat: latBand.ne, lng: 180 }, sw: { lat: latBand.sw, lng: -180 } };
  }

  return {
    ne: { lat: latBand.ne, lng: clamp(lng + lngDelta, 180) },
    sw: { lat: latBand.sw, lng: clamp(lng - lngDelta, 180) },
  };
};

/** Default search radius. Wide enough that a rural code is not an empty page. */
export const DEFAULT_RADIUS_METRES = 100000;

/**
 * The search URL for a located postal code.
 *
 * `bounds` is the parameter SearchPage.duck.js actually forwards to the listing
 * query. `address` rides along only so the search page can show the customer
 * what it searched for.
 */
export const searchUrlForLocation = ({ lat, lng, label, radiusMetres = DEFAULT_RADIUS_METRES }) => {
  const { ne, sw } = boundsAround(lat, lng, radiusMetres);
  const round = v => Number(v.toFixed(6));

  const params = new URLSearchParams({
    bounds: `${round(ne.lat)},${round(ne.lng)},${round(sw.lat)},${round(sw.lng)}`,
  });
  if (label) params.set('address', label);

  return `/s?${params.toString()}`;
};

/** Outcomes a caller has to tell apart to say anything useful to a customer. */
export const LOOKUP = {
  OK: 'ok',
  INVALID: 'invalid-postal-code',
  /** The provider answered, and has no coordinates for this code. */
  NOT_FOUND: 'not-found',
  /** The provider did not answer in time, or at all. */
  UNAVAILABLE: 'provider-unavailable',
};

/**
 * Translation keys, not copy. This module is a plain util and must not reach for
 * `useIntl`, so it names the message and the component resolves it — which is
 * also why the lookup returns a status rather than a sentence.
 *
 * The English values live in src/translations/en.json, with de, es and fr
 * alongside them per AGENTS.md. Their lead sentences are the copy
 * MapSearchSection already shipped and MapSearchSection.noKey.test.js pins;
 * rewording those was no part of this fix. The second sentence is the addition,
 * because a customer whose lookup failed still needs to be told how to shop.
 */
export const MESSAGE_IDS = {
  [LOOKUP.INVALID]: 'PostalSearch.invalidCode',
  [LOOKUP.NOT_FOUND]: 'PostalSearch.notFound',
  [LOOKUP.UNAVAILABLE]: 'PostalSearch.providerUnavailable',
};

const NOMINATIM = 'https://nominatim.openstreetmap.org/search';
const DEFAULT_TIMEOUT_MS = 6000;

/**
 * Nominatim's usage policy sets an absolute maximum of one request per second.
 *
 * This matters because of the two reliability paths above: a full-code miss
 * falls through to the FSA, and each shape may retry once, so one press of the
 * button can ask four times. On fast failures — a refused connection, an
 * immediate 5xx — those four land inside a few milliseconds, and the thing that
 * answers is a throttle or a block. That would make the lookup unavailable,
 * which is the exact fault this module exists to fix.
 *
 * Spacing is measured from the last request this page actually sent, so a first
 * search never waits and the common path (one request, it works) is unaffected.
 */
export const MIN_REQUEST_SPACING_MS = 1000;
let lastRequestAt = 0;

const realSleep = ms => new Promise(resolve => setTimeout(resolve, ms));
let sleepImpl = realSleep;

/**
 * Exported so component tests need not wait out the real second between
 * requests: a failing lookup makes four of them, which is three seconds a
 * suite would spend sleeping. The spacing itself is covered directly in
 * postalSearch.test.js with a sleep that records what it was asked to wait for.
 */
export const __setPostalSleep = fn => {
  sleepImpl = fn || realSleep;
};

/** One in-memory cache per page load, plus sessionStorage across navigations. */
const memoryCache = new Map();
const CACHE_PREFIX = 'postal-geocode:';

const readCache = key => {
  if (memoryCache.has(key)) return memoryCache.get(key);
  try {
    const raw = window.sessionStorage.getItem(CACHE_PREFIX + key);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    memoryCache.set(key, parsed);
    return parsed;
  } catch {
    // Private mode, blocked storage, corrupt entry. A cache that cannot be
    // read is a cache miss, never an error.
    return undefined;
  }
};

const writeCache = (key, value) => {
  memoryCache.set(key, value);
  try {
    window.sessionStorage.setItem(CACHE_PREFIX + key, JSON.stringify(value));
  } catch {
    // Storage full or unavailable. The in-memory copy still works.
  }
};

/** Exported so tests can start from a known state. */
export const __clearPostalCache = () => {
  memoryCache.clear();
  lastRequestAt = 0;
  try {
    Object.keys(window.sessionStorage)
      .filter(k => k.startsWith(CACHE_PREFIX))
      .forEach(k => window.sessionStorage.removeItem(k));
  } catch {
    /* nothing to clear */
  }
};

const fetchJson = async (url, { timeoutMs, fetchImpl, minSpacingMs, sleep }) => {
  // Wait out the remainder of the provider's one-per-second window, then record
  // this request before sending it: the mark has to go down even if the request
  // throws, or a run of fast failures spaces nothing.
  const waitFor = lastRequestAt + minSpacingMs - Date.now();
  if (waitFor > 0) await sleep(waitFor);
  lastRequestAt = Date.now();

  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
  try {
    const response = await fetchImpl(url, {
      headers: { 'Accept-Language': 'en' },
      signal: controller ? controller.signal : undefined,
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return await response.json();
  } finally {
    if (timer) clearTimeout(timer);
  }
};

const firstCoordinate = data => {
  const lat = parseFloat(data?.[0]?.lat);
  const lng = parseFloat(data?.[0]?.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  // Canada's extent, generously. A provider that answers with a point in
  // Australia for a Canadian postal code has matched something else, and
  // flying the map there is worse than saying we could not place it.
  if (lat < 41 || lat > 84 || lng < -142 || lng > -52) return null;
  return { lat, lng, label: data[0].display_name || null };
};

/**
 * Look up a Canadian postal code.
 *
 * Tries the full code, then the Forward Sortation Area. The FSA step is the
 * reliability fix: OpenStreetMap's coverage of full Canadian postal codes is
 * patchy because the data is proprietary, while FSA coverage is good. A
 * customer in R3C 0V8 gets Winnipeg either way, and never sees the difference.
 *
 * Retries once on a transient failure. Does not retry a clean "no result":
 * asking the same question twice does not change the answer, and the provider
 * asks not to be hammered.
 *
 * @returns {{status: String, lat?, lng?, label?, precision?: 'postal'|'fsa'}}
 */
export const lookupPostalCode = async (
  input,
  {
    timeoutMs = DEFAULT_TIMEOUT_MS,
    fetchImpl = typeof fetch === 'function' ? fetch : null,
    retries = 1,
    minSpacingMs = MIN_REQUEST_SPACING_MS,
    sleep = sleepImpl,
  } = {}
) => {
  const { valid, postal, fsa } = normalizePostal(input);
  if (!valid) return { status: LOOKUP.INVALID };
  if (!fetchImpl) return { status: LOOKUP.UNAVAILABLE };

  const cacheKey = postal || fsa;
  const cached = readCache(cacheKey);
  if (cached) return cached;

  // Full code first for precision, FSA second for coverage.
  const attempts = [
    postal ? { query: postal.replace(/\s/g, ''), precision: 'postal' } : null,
    { query: fsa, precision: 'fsa' },
  ].filter(Boolean);

  let sawProviderFailure = false;

  for (const { query, precision } of attempts) {
    for (let attempt = 0; attempt <= retries; attempt++) {
      const url = `${NOMINATIM}?postalcode=${encodeURIComponent(
        query
      )}&country=Canada&format=json&limit=1`;
      try {
        // eslint-disable-next-line no-await-in-loop
        const data = await fetchJson(url, { timeoutMs, fetchImpl, minSpacingMs, sleep });
        const found = firstCoordinate(data);
        if (found) {
          const result = { status: LOOKUP.OK, ...found, precision };
          writeCache(cacheKey, result);
          return result;
        }
        // A clean miss. Move to the next attempt shape rather than re-asking.
        break;
      } catch {
        sawProviderFailure = true;
        // Retry budget spent on this shape — fall through to the next one,
        // which may still succeed and is a better outcome than an error.
      }
    }
  }

  return { status: sawProviderFailure ? LOOKUP.UNAVAILABLE : LOOKUP.NOT_FOUND };
};
